import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Parse from 'parse';
import CreateEditCurrencyDialog from './dialog/CreateEditCurrencyDialog';
import DeleteCurrencyDialog from './dialog/DeleteCurrencyDialog';

/**
 * Display-currency catalogue table. Each currency maps to an internal
 * currency (SC or GC). System currencies (SC, GC) are marked and locked
 * from deletion.
 */
const CurrencyList = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Parse.Cloud.run('listCurrencies');
      if (result.success) setCurrencies(result.currencies);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load currencies',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleCreate = () => {
    setSelected(null);
    setDialogMode('create');
    setEditDialogOpen(true);
  };

  const handleEdit = (currency) => {
    setSelected(currency);
    setDialogMode('edit');
    setEditDialogOpen(true);
  };

  const handleDelete = (currency) => {
    setSelected(currency);
    setDeleteDialogOpen(true);
  };

  const handleSaved = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
    fetchCurrencies();
  };

  const handleToggleActive = async (currency) => {
    try {
      await Parse.Cloud.run('updateCurrency', {
        currencyId: currency.id,
        isActive: currency.isActive === false
      });
      fetchCurrencies();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update status',
        severity: 'error'
      });
    }
  };

  const headers = ['Code', 'Internal Currency', 'Name', 'Type', 'Status', 'Actions'];

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          onClick={handleCreate}
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: '#FFC107',
            color: '#000',
            textTransform: 'uppercase',
            '&:hover': { backgroundColor: '#FFD54F' }
          }}
        >
          Create Currency
        </Button>
      </Box>

      {/* Table */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              {headers.map((h) => (
                <Box
                  component="th"
                  key={h}
                  sx={{
                    p: 2,
                    textAlign: h === 'Actions' ? 'center' : 'left',
                    color: '#FFC107',
                    fontWeight: 600,
                    borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {loading ? (
              <Box component="tr">
                <Box component="td" colSpan={headers.length} sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </Box>
              </Box>
            ) : currencies.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={headers.length}
                  sx={{ p: 4, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}
                >
                  No currencies found
                </Box>
              </Box>
            ) : (
              currencies.map((c) => (
                <Box
                  component="tr"
                  key={c.id}
                  sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
                >
                  <Box component="td" sx={cellSx}>
                    <strong style={{ color: '#FFC107' }}>{c.code}</strong>
                  </Box>
                  <Box component="td" sx={cellSx}>
                    {c.internalCurrency}
                  </Box>
                  <Box component="td" sx={cellSx}>
                    {c.name || '-'}
                  </Box>
                  <Box component="td" sx={cellSx}>
                    <Chip
                      label={c.isSystem ? 'System' : 'Custom'}
                      size="small"
                      sx={{
                        backgroundColor: c.isSystem
                          ? 'rgba(33,150,243,0.2)'
                          : 'rgba(255,255,255,0.1)',
                        color: c.isSystem ? '#2196F3' : '#fff'
                      }}
                    />
                  </Box>
                  <Box component="td" sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={c.isActive !== false}
                        onChange={() => handleToggleActive(c)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#4CAF50' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4CAF50'
                          }
                        }}
                      />
                      <Chip
                        label={c.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor:
                            c.isActive !== false ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                          color: c.isActive !== false ? '#4CAF50' : '#F44336'
                        }}
                      />
                    </Box>
                  </Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                    <IconButton
                      onClick={() => handleEdit(c)}
                      size="small"
                      sx={{ color: '#FFC107', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Tooltip title={c.isSystem ? 'System currency cannot be deleted' : 'Delete'}>
                      <span>
                        <IconButton
                          onClick={() => handleDelete(c)}
                          size="small"
                          disabled={c.isSystem}
                          sx={{ color: c.isSystem ? 'rgba(255,255,255,0.2)' : '#F44336' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>

      <CreateEditCurrencyDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSaved={handleSaved}
        currency={selected}
        mode={dialogMode}
      />

      <DeleteCurrencyDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={handleSaved}
        currency={selected}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const cellSx = {
  p: 2,
  color: '#fff',
  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  whiteSpace: 'nowrap'
};

export default CurrencyList;
